import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowRight,
  Eye,
  EyeOff,
  Info,
  Plus,
  RefreshCw,
  Users,
} from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import { MetricTile } from '@modules/_shared/components/document-stats/MetricTile';
import {
  type DailyOpen,
  OpensBarChart,
  buildChartData,
  buildStatsRange,
} from '@modules/_shared/components/document-stats/OpensBarChart';
import { getVisitorCountState } from '@modules/_shared/components/document-stats/visitorCountState';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { useLocale } from '@shared/hooks/locale';
import type { Language } from '@shared/LocaleContext';
import { toGlobalId } from '@shared/utils/global-id';

import {
  type BrochureRecipient,
  getRecipientOpenTime,
} from './brochureRecipients';
import { BrochureRecipientsDrawer } from './BrochureRecipientsDrawer';
import { GET_DOCUMENT_RECIPIENTS } from './brochureRecipientsQueries';
import { BuyerLinkDialog } from './BuyerLinkDialog';
import { LISTING_BROCHURE_ANCHORS } from './listingBrochureTour';

const GET_BROCHURE_STATS = gql(/* GraphQL */ `
  query GetListingBrochureStats(
    $documentId: String!
    $from: timestamptz
    $to: timestamptz
  ) {
    document_stats(
      document_id: $documentId
      document_type: "lot_brochure"
      from: $from
      to: $to
    ) {
      total_opens
      unique_opens
      unidentified_opens
      daily {
        date
        total
        unique
      }
    }
  }
`);

const RANGE_DAYS = 30;
const MAX_RECENT_VIEWERS = 3;
const STATS_REFRESH_INTERVAL_MS = 60_000;

const RetryAlert = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => {
  const { t } = useLocale();

  return (
    <Alert
      severity="warning"
      action={
        <Button
          color="inherit"
          size="small"
          startIcon={<RefreshCw size={16} />}
          onClick={onRetry}
        >
          {t('Retry')}
        </Button>
      }
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {message}
      </Typography>
    </Alert>
  );
};

const ViewerRow = ({ recipient }: { recipient: BrochureRecipient }) => {
  const { t } = useLocale();

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      <UserAvatar
        nameFallback={recipient.name?.trim() || '?'}
        size={28}
        sx={{ fontSize: 12 }}
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flex: 1,
          minWidth: 0,
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {recipient.name ?? t('Unnamed buyer')}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 0 }}
        >
          {t('{{count}} view', { count: recipient.total_opens })}
          {recipient.last_open_at != null && (
            <>
              {' · '}
              <TimeAgo dateString={recipient.last_open_at} addSuffix />
            </>
          )}
        </Typography>
      </Stack>
    </Stack>
  );
};

type BrochureViewsCardProps = {
  lotId: string;
  templateId: string | null;
  language: Language;
  readOnly?: boolean;
};

export const BrochureViewsCard = ({
  lotId,
  templateId,
  language,
  readOnly = false,
}: BrochureViewsCardProps) => {
  const { t, locale } = useLocale();
  const [showRecipients, setShowRecipients] = useState(false);
  const [showBuyerLinkDialog, setShowBuyerLinkDialog] = useState(false);

  const documentId = useMemo(() => toGlobalId('Lot', lotId), [lotId]);
  const [statsRange, setStatsRange] = useState(() =>
    buildStatsRange(RANGE_DAYS),
  );

  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery(GET_BROCHURE_STATS, {
    variables: { documentId, from: statsRange.from },
    skip: lotId === '',
    fetchPolicy: 'cache-and-network',
    pollInterval: templateId != null ? STATS_REFRESH_INTERVAL_MS : 0,
    skipPollAttempt: () => {
      if (document.hidden) {
        return true;
      }
      const nextRange = buildStatsRange(RANGE_DAYS);
      if (nextRange.from !== statsRange.from) {
        setStatsRange(nextRange);
        // The changed variables fetch the new day; don't also poll the old range.
        return true;
      }
      return false;
    },
  });

  const {
    data: recipientsData,
    error: recipientsError,
    refetch: refetchRecipients,
  } = useQuery(GET_DOCUMENT_RECIPIENTS, {
    variables: {
      documentId,
      documentType: Doc_Template_Type_Enum_Enum.LotBrochure,
    },
    skip: lotId === '',
    fetchPolicy: 'cache-and-network',
    pollInterval: templateId != null ? STATS_REFRESH_INTERVAL_MS : 0,
    skipPollAttempt: () => document.hidden,
  });

  const stats = statsData?.document_stats;
  const totalOpens = stats?.total_opens ?? 0;
  const uniqueOpens = stats?.unique_opens ?? 0;
  const peopleState = getVisitorCountState(stats);
  const peopleValue =
    peopleState === 'unavailable'
      ? t('Unavailable')
      : peopleState === 'incomplete'
      ? t('{{count}} · Incomplete', { count: uniqueOpens })
      : uniqueOpens;
  const isInitialStatsLoading = statsLoading && stats == null;

  const chartData = useMemo<DailyOpen[]>(
    () => buildChartData(stats?.daily ?? [], RANGE_DAYS, statsRange.to),
    [stats, statsRange.to],
  );

  const recipients = useMemo(
    () => recipientsData?.document_recipients?.recipients ?? [],
    [recipientsData],
  );
  const sharedCount = recipients.length;

  const recentViewers = useMemo(
    () =>
      recipients
        .filter(recipient => recipient.total_opens > 0)
        .sort((a, b) => getRecipientOpenTime(b) - getRecipientOpenTime(a))
        .slice(0, MAX_RECENT_VIEWERS),
    [recipients],
  );

  return (
    <Paper
      {...tourAnchor(LISTING_BROCHURE_ANCHORS.views)}
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('Brochure views')}
            </Typography>
            <Tooltip
              title={t(
                'View counts may take a few minutes to update. Opens made while signed in to the CRM are excluded. Use an incognito window to verify a link. Links sent by mass email or enquiry auto-reply are counted per buyer automatically.',
              )}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                aria-label={t('Brochure views')}
                sx={{ p: 0, color: 'text.disabled' }}
              >
                <Info size={16} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t('Last {{days}} days', { days: RANGE_DAYS })}
          </Typography>
        </Stack>

        {statsError != null ? (
          <RetryAlert
            message={t('View stats are temporarily unavailable')}
            onRetry={() => {
              refetchStats().catch(() => {});
            }}
          />
        ) : (
          <>
            <Stack direction="row" spacing={2}>
              <MetricTile
                icon={Eye}
                label={t('Views')}
                value={totalOpens}
                loading={isInitialStatsLoading}
              />
              <MetricTile
                icon={Users}
                label={t('People')}
                value={peopleValue}
                loading={isInitialStatsLoading}
              />
            </Stack>

            {/* When buyer links exist, the "Viewed by" section below already
                carries the zero-views message — avoid saying it twice. */}
            {!isInitialStatsLoading &&
              (totalOpens === 0 ? (
                sharedCount === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('No views yet. Share the brochure to see views here.')}
                  </Typography>
                )
              ) : (
                <Box
                  sx={{
                    width: 'calc(100% + 24px)',
                    transform: 'translateX(-24px)',
                  }}
                >
                  <OpensBarChart
                    data={chartData}
                    totalOpensLabel={t('Views')}
                    uniqueOpensLabel={t('People')}
                    locale={locale}
                    height={120}
                    showUniqueSplit={peopleState === 'complete'}
                  />
                </Box>
              ))}
          </>
        )}

        {recipientsError != null && (
          <RetryAlert
            message={t('We could not load views by buyer. Try again.')}
            onRetry={() => {
              refetchRecipients().catch(() => {});
            }}
          />
        )}

        {sharedCount > 0 && (
          <>
            <Divider />
            <Stack spacing={1.5}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">{t('Buyer links')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Last {{days}} days', { days: 365 })}
                </Typography>
              </Stack>
              {recentViewers.length === 0 ? (
                <Stack
                  direction="row"
                  spacing={1.25}
                  sx={{ alignItems: 'center' }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <EyeOff size={16} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('No views yet')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Views appear here once a buyer opens their link.')}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                recentViewers.map(recipient => (
                  <ViewerRow key={recipient.user_id} recipient={recipient} />
                ))
              )}
              <ButtonBase
                onClick={() => setShowRecipients(true)}
                sx={{
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'primary.main',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  borderRadius: '4px',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t('All buyers ({{count}})', { count: sharedCount })}
                <ArrowRight size={14} />
              </ButtonBase>
            </Stack>
          </>
        )}

        {templateId != null && !readOnly && (
          <>
            <Divider />
            <Button
              {...tourAnchor(LISTING_BROCHURE_ANCHORS.buyerLink)}
              variant="text"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => setShowBuyerLinkDialog(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('Create brochure link for a buyer')}
            </Button>
          </>
        )}
      </Stack>

      {showRecipients && (
        <BrochureRecipientsDrawer
          recipients={recipients}
          documentId={documentId}
          templateId={templateId}
          language={language}
          readOnly={readOnly}
          onClose={() => setShowRecipients(false)}
        />
      )}

      {showBuyerLinkDialog && templateId != null && !readOnly && (
        <BuyerLinkDialog
          lotId={lotId}
          templateId={templateId}
          language={language}
          onClose={() => setShowBuyerLinkDialog(false)}
          onLinkCreated={() => {
            refetchRecipients().catch(() => {});
          }}
        />
      )}
    </Paper>
  );
};
